import { useEffect, useMemo, useState } from "react";
import { KeyRound, Plus, RotateCcw, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TableSkeleton } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SelectField, TextField } from "@/components/forms/Fields";
import { cn } from "@/lib/utils";

const ACTION_LABELS = { view: "View", create: "Create", edit: "Edit", delete: "Delete" };

function useModules() {
  const [modules, setModules] = useState(null);
  useEffect(() => {
    api.get("/permissions/modules").then((r) => setModules(r.data.modules)).catch(() => setModules([]));
  }, []);
  return modules;
}

function MatrixGrid({ modules, value, onToggle, disabled, mode = "role", overrides }) {
  const groups = useMemo(() => {
    const out = {};
    modules.forEach((m) => { (out[m.group] = out[m.group] || []).push(m); });
    return out;
  }, [modules]);

  return (
    <div className="space-y-5">
      {Object.entries(groups).map(([group, mods]) => (
        <div key={group} className="space-y-2">
          <p className="label-xs">{group}</p>
          <div className="overflow-hidden rounded-md border border-slate-200">
            {mods.map((m) => (
              <div key={m.key} data-testid={`perm-row-${m.key}`}
                className="flex flex-col gap-2 border-b border-slate-100 px-4 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-800">{m.label}</p>
                <div className="flex flex-wrap gap-4">
                  {m.actions.map((a) => {
                    const checked = (value[m.key] || []).includes(a);
                    const overridden = mode === "user" && overrides?.[m.key]?.[a] !== undefined;
                    return (
                      <label key={a} className={cn("flex items-center gap-2 text-[11px]",
                        overridden ? "text-blue-700" : "text-slate-500")}>
                        <Checkbox
                          checked={checked}
                          disabled={disabled}
                          onCheckedChange={(v) => onToggle(m.key, a, !!v)}
                          data-testid={`perm-${m.key}-${a}`}
                        />
                        {ACTION_LABELS[a]}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function RolesTab() {
  const { can, refreshUser } = useAuth();
  const modules = useModules();
  const [roles, setRoles] = useState(null);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState({});
  const [creating, setCreating] = useState(false);
  const [newRole, setNewRole] = useState({ label: "", copy_from: "recruiter" });
  const editable = can("team", "edit");

  const load = async (keep) => {
    try {
      const { data } = await api.get("/roles");
      setRoles(data.items);
      const next = data.items.find((r) => r.key === (keep || active)) || data.items[0];
      setActive(next?.key);
      setDraft(next?.permissions || {});
    } catch (e) {
      setRoles([]);
      toast.error(apiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const select = (role) => {
    setActive(role.key);
    setDraft(role.permissions || {});
  };

  const toggle = (module, action, on) => {
    setDraft((d) => {
      const current = new Set(d[module] || []);
      on ? current.add(action) : current.delete(action);
      return { ...d, [module]: [...current] };
    });
  };

  const current = roles?.find((r) => r.key === active);

  const save = async () => {
    try {
      await api.put(`/roles/${active}`, { permissions: draft, label: current?.label });
      toast.success(`${current?.label} privileges saved`);
      await load(active);
      refreshUser();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const reset = async () => {
    try {
      await api.post(`/roles/${active}/reset`);
      toast.success("Reset to default privileges");
      await load(active);
      refreshUser();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/roles/${active}`);
      toast.success("Role deleted");
      setActive(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const create = async () => {
    if (!newRole.label.trim()) return toast.error("Give the role a name");
    try {
      const { data } = await api.post("/roles", newRole);
      toast.success(`${data.label} created`);
      setCreating(false);
      setNewRole({ label: "", copy_from: "recruiter" });
      await load(data.key);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!roles || !modules) return <TableSkeleton rows={6} cols={4} />;

  return (
    <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
      <aside className="space-y-2">
        {editable && (
          <Button onClick={() => setCreating(true)} data-testid="create-role-button"
            className="h-9 w-full gap-2 rounded-full bg-blue-600 text-xs hover:bg-blue-500">
            <Plus className="h-3.5 w-3.5" /> New role
          </Button>
        )}
        <div className="surface overflow-hidden rounded-md">
          {roles.map((r) => (
            <button key={r.key} onClick={() => select(r)} data-testid={`role-item-${r.key}`}
              className={cn("flex w-full items-center justify-between gap-2 border-b border-slate-100 px-4 py-3 text-left last:border-0 hover:bg-slate-50",
                r.key === active && "bg-blue-50")}>
              <span className="min-w-0">
                <span className="block truncate text-sm text-slate-800">{r.label}</span>
                <span className="block text-[10px] text-slate-400">
                  {r.builtin ? "Built-in" : "Custom"} · {r.user_count} user{r.user_count === 1 ? "" : "s"}
                </span>
              </span>
              {r.locked && <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
            </button>
          ))}
        </div>
      </aside>

      <section className="surface space-y-5 rounded-md p-6" data-testid="role-matrix">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="label-xs">Module privileges</p>
            <h3 className="mt-1 text-base font-medium text-slate-900">{current?.label}</h3>
          </div>
          {editable && current && !current.locked && (
            <div className="flex flex-wrap gap-2">
              {current.builtin ? (
                <Button variant="outline" size="sm" onClick={reset} data-testid="reset-role-button"
                  className="h-8 gap-1.5 rounded-full border-slate-200 text-xs text-slate-600">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset to default
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={remove} data-testid="delete-role-button"
                  className="h-8 gap-1.5 rounded-full border-rose-200 text-xs text-rose-700 hover:bg-rose-50">
                  <Trash2 className="h-3.5 w-3.5" /> Delete role
                </Button>
              )}
              <Button size="sm" onClick={save} data-testid="save-role-button"
                className="h-8 rounded-full bg-blue-600 px-4 text-xs hover:bg-blue-500">
                Save privileges
              </Button>
            </div>
          )}
        </div>

        {current?.locked ? (
          <p className="rounded border border-emerald-200 bg-emerald-50 p-4 text-xs text-emerald-800">
            Super Admin always retains full access to every module. This cannot be restricted.
          </p>
        ) : (
          <MatrixGrid modules={modules} value={draft} onToggle={toggle} disabled={!editable} />
        )}
      </section>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-md border-slate-200 bg-white">
          <DialogHeader><DialogTitle className="text-base text-slate-900">Create a custom role</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <TextField label="Role name" value={newRole.label} required testid="new-role-name-input"
              onChange={(v) => setNewRole((f) => ({ ...f, label: v }))} hint="e.g. Semiconductor Delivery Lead" />
            <SelectField label="Start from" value={newRole.copy_from} testid="new-role-copy-select"
              onChange={(v) => setNewRole((f) => ({ ...f, copy_from: v }))}
              options={roles.map((r) => ({ value: r.key, label: r.label }))} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)} className="text-slate-500">Cancel</Button>
            <Button onClick={create} data-testid="submit-role-button" className="rounded-full bg-blue-600 px-5 hover:bg-blue-500">
              Create role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function UserPrivilegeDialog({ user, open, onOpenChange, onSaved }) {
  const modules = useModules();
  const [data, setData] = useState(null);
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    if (!open || !user) return;
    setData(null);
    api.get(`/users/${user.id}/permissions`)
      .then((r) => { setData(r.data); setOverrides(r.data.permission_overrides || {}); })
      .catch((e) => toast.error(apiError(e)));
  }, [open, user]);

  const effective = useMemo(() => {
    if (!data) return {};
    const out = {};
    Object.entries(data.role_permissions || {}).forEach(([k, v]) => { out[k] = [...v]; });
    Object.entries(overrides).forEach(([k, actions]) => {
      const set = new Set(out[k] || []);
      Object.entries(actions).forEach(([a, allow]) => (allow ? set.add(a) : set.delete(a)));
      out[k] = [...set];
    });
    return out;
  }, [data, overrides]);

  const toggle = (module, action, on) => {
    const inherited = (data?.role_permissions?.[module] || []).includes(action);
    setOverrides((o) => {
      const next = { ...o, [module]: { ...(o[module] || {}) } };
      if (on === inherited) delete next[module][action];
      else next[module][action] = on;
      if (!Object.keys(next[module]).length) delete next[module];
      return next;
    });
  };

  const save = async () => {
    try {
      await api.put(`/users/${user.id}/permissions`, { permission_overrides: overrides });
      toast.success(`Privileges updated for ${user.name}`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open && !!user} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto border-slate-200 bg-white thin-scroll">
        <DialogHeader>
          <DialogTitle className="text-base text-slate-900">Privileges — {user?.name}</DialogTitle>
        </DialogHeader>
        {user?.role === "super_admin" ? (
          <p className="rounded border border-emerald-200 bg-emerald-50 p-4 text-xs text-emerald-800">
            Super Admin always retains full access to every module. Privileges cannot be restricted for this account.
          </p>
        ) : !modules || !data ? (
          <TableSkeleton rows={5} cols={3} />
        ) : (
          <div className="space-y-4">
            <p className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
              Inherited from <span className="text-slate-800">{data.user.role_label}</span>. Anything you change here
              applies to this user only and is highlighted in blue.
            </p>
            <MatrixGrid modules={modules} value={effective} onToggle={toggle} mode="user" overrides={overrides} />
          </div>
        )}
        <DialogFooter>
          {user?.role !== "super_admin" && (
            <>
              <Button variant="ghost" onClick={() => setOverrides({})} className="text-slate-500" data-testid="clear-overrides-button">
                Clear overrides
              </Button>
              <Button onClick={save} data-testid="save-user-perms-button" className="rounded-full bg-blue-600 px-5 hover:bg-blue-500">
                Save privileges
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditUserDialog({ user, open, onOpenChange, onSaved }) {
  const { meta } = useAuth();
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({});
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (!open || !user) return;
    setForm({ name: user.name || "", title: user.title || "", role: user.role, client_id: user.client_id || null });
    setPassword("");
    api.get("/roles").then((r) => setRoles(r.data.items)).catch(() => setRoles([]));
  }, [open, user]);

  const save = async () => {
    try {
      await api.patch(`/users/${user.id}`, { ...form, password: password || undefined });
      toast.success("User updated");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-slate-200 bg-white">
        <DialogHeader><DialogTitle className="text-base text-slate-900">Edit user</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <TextField label="Full name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testid="edit-user-name-input" />
          <TextField label="Title" value={form.title} onChange={(v) => setForm((f) => ({ ...f, title: v }))} />
          <SelectField label="Role" value={form.role} onChange={(v) => setForm((f) => ({ ...f, role: v }))}
            options={roles.map((r) => ({ value: r.key, label: r.label }))} testid="edit-user-role-select" />
          {form.role === "client" && (
            <SelectField label="Client organisation" value={form.client_id} testid="edit-user-client-select"
              onChange={(v) => setForm((f) => ({ ...f, client_id: v }))}
              options={(meta?.clients || []).map((c) => ({ value: c.id, label: c.name }))} />
          )}
          <TextField label="Reset password" value={password} onChange={setPassword} testid="edit-user-password-input"
            hint="Leave blank to keep the current password." />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500">Cancel</Button>
          <Button onClick={save} data-testid="save-user-button" className="gap-2 rounded-full bg-blue-600 px-5 hover:bg-blue-500">
            <KeyRound className="h-3.5 w-3.5" /> Save user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
